import React, { useMemo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { formatDateSlashesWithClock, isBetween } from '~/services/TimeService';

import { CampaignScheduleDropdownRow } from './CampaignScheduleExpandableRow';
import { buildCampaignScheduleBrandRows } from './campaignScheduleBrandRows';
import { BRAND_ROWS_TABLE_COLUMN_COUNT, handleExpandRowKeyDown, toggleExpandedRow } from './campaignScheduleTabLayout';
import type { BrandSchedule } from './types';

function isRowActive(start: string, end: string) {
  return isBetween(start, end, new Date(), undefined, '[]');
}

interface Props {
  emptyMessage: string;
  schedules: Array<BrandSchedule>;
}

export default function CampaignScheduleBrandRowsTable({ emptyMessage, schedules }: Props) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const brandRows = useMemo(() => buildCampaignScheduleBrandRows(schedules), [schedules]);

  if (brandRows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }} variant="body2">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Schedule</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Dates</TableCell>
          <TableCell>Regions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {brandRows.map((row) => {
          const isExpanded = expandedRows.has(row.key);
          const isActive = isRowActive(row.start, row.end);

          return (
            <React.Fragment key={row.key}>
              <TableRow>
                <TableCell>
                  <Typography fontWeight={700} variant="body2">
                    {row.brand}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    color={row.isLuxPlus ? undefined : 'info'}
                    label={row.isLuxPlus ? 'LuxPlus' : 'Normal'}
                    size="small"
                    sx={row.isLuxPlus ? { color: '#9c27b0', borderColor: '#9c27b0' } : undefined}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    color={isActive ? 'success' : 'default'}
                    label={isActive ? 'Active' : 'Inactive'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    {row.pill === 'available_offline' && (
                      <Chip
                        color="warning"
                        label="Available Offline"
                        size="small"
                        sx={{ alignSelf: 'flex-start' }}
                        variant="outlined"
                      />
                    )}
                    <Typography variant="body2">
                      {formatDateSlashesWithClock(row.start)} → {formatDateSlashesWithClock(row.end)}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack
                    alignItems="center"
                    direction="row"
                    role="button"
                    spacing={0.5}
                    sx={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onClick={() => setExpandedRows((current) => toggleExpandedRow(current, row.key))}
                    onKeyDown={(event) =>
                      handleExpandRowKeyDown(event, () =>
                        setExpandedRows((current) => toggleExpandedRow(current, row.key)),
                      )
                    }
                  >
                    <Typography variant="body2">
                      {row.regions.length === 1 ? row.regions[0] : `${row.regions.length} regions`}
                    </Typography>
                    {isExpanded ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowRightIcon fontSize="small" />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
              <CampaignScheduleDropdownRow colSpan={BRAND_ROWS_TABLE_COLUMN_COUNT} isExpanded={isExpanded}>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {row.regions.map((region) => (
                    <Chip key={region} label={region} size="small" variant="outlined" />
                  ))}
                </Stack>
              </CampaignScheduleDropdownRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
